import React, { useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Row, Col, Image, Card, Badge, Container, Accordion } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useGetCompanyByRucQuery } from '../../slices/companyApiSlice';
import { useGetProductsByCompanyQuery } from '../../slices/productApiSlice';
import ProductCard from '../../components/product/ProductCard';
import ProductCardSkeleton from '../../components/ui/ProductCardSkeleton';
import Message from '../../components/layout/Message';
import Paginate from '../../components/ui/Paginate';
import SortSelect from '../../components/ui/SortSelect';
import Meta from '../../components/layout/Meta';
import CategoryFilter from '../../components/ui/CategoryFilter';
import ActiveGlobalDiscountBanner from '../../components/ui/ActiveGlobalDiscountBanner';
import ShippingBehaviorBanner from '../../components/ui/ShippingBehaviorBanner';
import Skeleton from '../../components/ui/Skeleton';
import { 
    FaMapMarkerAlt,
    FaTruck, 
    FaClock, 
    FaWhatsapp, 
    FaInstagram, 
    FaFacebook, 
    FaTiktok, 
    FaExternalLinkAlt,
    FaMoneyBillWave,
    FaBuilding,
    FaFilter
} from 'react-icons/fa';

const CompanyScreen = () => {
    const { ruc, pageNumber } = useParams();
    const { userInfo } = useSelector((state) => state.auth);

    const [searchParams, setSearchParams] = useSearchParams();
    const sort = searchParams.get('sort') || '';
    const category = searchParams.get('category') || '';
    const page = pageNumber || 1;

    const {
        data: company,
        isLoading: isLoadingCompany,
        error: companyError,
    } = useGetCompanyByRucQuery(ruc);

    const {
        data: productsData,
        isLoading: isLoadingProducts,
        isFetching: isFetchingProducts,
        error: productsError,
    } = useGetProductsByCompanyQuery({
        ruc,
        pageNumber: page,
        priceAssigned: userInfo?.priceAssigned || null,
        sort,
        category,
    });

    const products = productsData?.products || [];
    const productsSectionRef = useRef(null);

    useEffect(() => {
        if (
            productsSectionRef.current &&
            productsData &&
            productsData.page > 1 &&
            !isLoadingProducts &&
            !isFetchingProducts &&
            !productsError &&
            productsData?.products?.length > 0
        ) {
            setTimeout(() => {
                productsSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 50);
        }
    }, [productsData, productsData?.page, productsData?.products?.length, isLoadingProducts, isFetchingProducts, productsError]);

    const handleCategoryChange = (selectedCategory) => {
        const newParams = new URLSearchParams(searchParams);
        if (selectedCategory) {
            newParams.set('category', selectedCategory);
        } else {
            newParams.delete('category');
        }
        // Reset page to 1 when filter changes
        newParams.delete('page'); 
        setSearchParams(newParams);
    };

    if (isLoadingCompany) return (
        <div className="py-4">
            <div className="mb-4 border-0 shadow-sm rounded-4 overflow-hidden p-4 bg-white">
                <div className="d-flex align-items-center gap-3">
                    <Skeleton width="100px" height="100px" borderRadius="12px" style={{ flexShrink: 0 }} />
                    <div className="flex-grow-1">
                        <Skeleton width="60%" height="2em" style={{ marginBottom: 8 }} />
                        <Skeleton width="40%" height="1.2em" />
                        <div className="d-flex gap-2 mt-2">
                            <Skeleton width="80px" height="1.2em" />
                            <Skeleton width="80px" height="1.2em" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
    if (companyError) return <Message variant="danger">Error al cargar la información de la tienda</Message>;

    const customColor = '#b5430f';

    const SocialLink = ({ href, icon: Icon, label, color }) => {
        if (!href) return null;
        // Ensure URL has protocol
        const url = href.startsWith('http') ? href : `https://${href}`;
        return (
            <a 
                href={url} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-decoration-none me-3 d-inline-flex align-items-center social-link-hover"
                style={{ color: color || '#555', transition: 'all 0.2s' }}
            >
                <Icon size={24} className="me-1" />
                <span className="d-none d-md-inline">{label}</span>
            </a>
        );
    };

    return (
        <Container className="py-4">
            <Meta title={company.tradeName} description={company.description} />
            <ActiveGlobalDiscountBanner companyRuc={company?.ruc || ruc} />
            <ShippingBehaviorBanner
                companyRuc={company?.ruc || ruc}
                policy={company?.shippingPolicy}
                companyName={company?.tradeName}
            />
            
            <style>
                {`
                    .custom-accordion .accordion-button:not(.collapsed) {
                        color: ${customColor};
                        background-color: ${customColor}11;
                        box-shadow: inset 0 -1px 0 rgba(0,0,0,.125);
                    }
                    .custom-accordion .accordion-button:focus {
                        border-color: ${customColor}80;
                        box-shadow: 0 0 0 0.25rem ${customColor}40;
                    }
                    .custom-accordion .accordion-button::after {
                        background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23b5430f'%3e%3cpath fill-rule='evenodd' d='M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z'/%3e%3c/svg%3e");
                    }
                `}
            </style>

            {/* Header Section */}
            <Card className="mb-4 border-0 shadow-sm rounded-4 overflow-hidden">
                <Card.Body className="p-4">
                    <Row className="align-items-center g-3">
                        <Col xs={12} md={8}>
                            <div className="d-flex align-items-center">
                                <div className="bg-white border rounded-3 p-1 shadow-sm me-3 flex-shrink-0" style={{ width: '100px', height: '100px' }}>
                                    <Image
                                        src={company.logoUrl || '/images/default-company.png'}
                                        alt={company.tradeName}
                                        className="w-100 h-100 object-fit-contain rounded-2"
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = '/images/default-company.png';
                                        }}
                                    />
                                </div>
                                <div>
                                    <h1 className="fw-bold mb-2 h3" style={{ color: '#2c3e50' }}>{company.tradeName}</h1>
                                    <div className="d-flex flex-wrap gap-2">
                                        <Badge bg="light" text="dark" className="border d-flex align-items-center fw-normal px-3 py-2 rounded-pill">
                                            <FaBuilding className="me-2 text-muted" /> {company.legalName}
                                        </Badge>
                                        <Badge bg="light" text="dark" className="border d-flex align-items-center fw-normal px-3 py-2 rounded-pill">
                                            <FaMapMarkerAlt className="me-2 text-danger" /> {company.city}
                                        </Badge>
                                    </div>
                                </div>
                            </div>
                        </Col>
                        
                        {company.location?.coordinates?.lat && company.location?.coordinates?.lng && (
                            <Col xs={12} md={4} className="d-none d-md-block">
                                <div className="rounded-3 overflow-hidden border shadow-sm" style={{ height: '120px' }}>
                                    <iframe
                                        title="Company Location"
                                        width="100%"
                                        height="100%"
                                        style={{ border: 0 }}
                                        src={`https://maps.google.com/maps?q=${company.location.coordinates.lat},${company.location.coordinates.lng}&hl=es&z=14&output=embed`}
                                    ></iframe>
                                </div>
                            </Col>
                        )}
                    </Row>
                </Card.Body>
            </Card>

            <Row className="g-4">
                <Col lg={8}>
                    <div className="mb-4">
                        <h5 className="fw-bold border-bottom pb-2 mb-3" style={{ color: customColor }}>Sobre Nosotros</h5>
                        <p className="text-muted" style={{ whiteSpace: 'pre-line', lineHeight: '1.6' }}>
                            {company.description || 'Sin descripción disponible.'}
                        </p>
                    </div>

                    {/* Location & Contact */}
                    <div className="mb-4">
                        <h5 className="fw-bold border-bottom pb-2 mb-3" style={{ color: customColor }}>Ubicación y Contacto</h5>
                        <div className="bg-light p-4 rounded-4 border">
                            {company.location?.address && (
                                <p className="mb-3 d-flex align-items-start">
                                    <FaMapMarkerAlt className="me-2 mt-1 text-danger flex-shrink-0" />
                                    <span className="text-dark">{company.location.address}</span>
                                </p>
                            )}
                            
                            {company.location?.googleMapsUrl && (
                                <div className="mb-4">
                                    <a 
                                        href={company.location.googleMapsUrl} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="btn btn-sm btn-outline-primary d-inline-flex align-items-center rounded-pill px-3"
                                    >
                                        <FaExternalLinkAlt className="me-2" /> Ver en Google Maps
                                    </a>
                                </div>
                            )}

                            <div className="d-flex flex-wrap gap-3 mt-3 pt-3 border-top">
                                {company.socialMedia?.whatsapp && (
                                    <SocialLink 
                                        href={`https://wa.me/${company.socialMedia.whatsapp.replace(/[^0-9]/g, '')}`} 
                                        icon={FaWhatsapp} 
                                        label="WhatsApp" 
                                        color="#25D366" 
                                    />
                                )}
                                <SocialLink href={company.socialMedia?.instagram} icon={FaInstagram} label="Instagram" color="#E1306C" />
                                <SocialLink href={company.socialMedia?.facebook} icon={FaFacebook} label="Facebook" color="#1877F2" />
                                <SocialLink href={company.socialMedia?.tiktok} icon={FaTiktok} label="TikTok" color="#000000" />
                            </div>
                        </div>
                    </div>
                </Col>

                <Col lg={4}>
                    {/* Info Cards Sidebar */}
                    <div className="d-flex flex-column gap-3">
                        {/* Shipping Policy */}
                        <Card className="border-0 shadow-sm h-100 rounded-4">
                            <Card.Body className="p-4">
                                <div className="d-flex align-items-center mb-3">
                                    <div className="p-2 rounded-circle bg-opacity-10 me-3" style={{ backgroundColor: `${customColor}22` }}>
                                        <FaTruck style={{ color: customColor }} size={20} />
                                    </div>
                                    <h6 className="fw-bold mb-0">Política de Envío</h6>
                                </div>
                                <p className="small text-muted mb-0">
                                    {company.shippingPolicy?.type === 'FREE_OVER_AMOUNT'
                                        ? <>Envío gratis en productos elegibles desde <strong>${Number(company.shippingPolicy.freeShippingThreshold || 0).toFixed(2)}</strong>. Toda la compra ayuda a llegar.</>
                                        : 'El envío se cobra. No hay envío gratis por monto.'}
                                </p>
                            </Card.Body>
                        </Card>

                        {/* Payment Methods */}
                        <Card className="border-0 shadow-sm h-100 rounded-4">
                            <Card.Body className="p-4">
                                <div className="d-flex align-items-center mb-3">
                                    <div className="p-2 rounded-circle bg-opacity-10 me-3" style={{ backgroundColor: `${customColor}22` }}>
                                        <FaMoneyBillWave style={{ color: customColor }} size={20} />
                                    </div>
                                    <h6 className="fw-bold mb-0">Métodos de Pago</h6>
                                </div>
                                <div className="d-flex flex-wrap gap-2">
                                    {company.paymentMethods?.map((method) => (
                                        <Badge key={method} bg="light" text="dark" className="border fw-normal px-2 py-1">
                                            {method === 'BANK_TRANSFER' ? 'Transferencia' : 'PayPhone'}
                                        </Badge>
                                    ))}
                                </div>
                            </Card.Body>
                        </Card>

                        {/* Opening Hours Accordion */}
                        {company.openingHours && company.openingHours.length > 0 && (
                            <Accordion className="shadow-sm border-0 custom-accordion mb-4 rounded-4 overflow-hidden">
                                <Accordion.Item eventKey="0" className="border-0">
                                    <Accordion.Header>
                                        <div className="d-flex align-items-center w-100">
                                            <div className="p-2 rounded-circle bg-opacity-10 me-3" style={{ backgroundColor: `${customColor}22` }}>
                                                <FaClock style={{ color: customColor }} size={20} />
                                            </div>
                                            <h6 className="fw-bold mb-0 flex-grow-1">Horarios de Atención</h6>
                                        </div>
                                    </Accordion.Header>
                                    <Accordion.Body className="pt-3 pb-3 bg-white">
                                        <ul className="list-unstyled small text-muted mb-0">
                                            {company.openingHours.map((hour, index) => (
                                                <li key={index} className="mb-2 border-bottom pb-2 last-no-border">
                                                    {hour}
                                                </li>
                                            ))}
                                        </ul>
                                    </Accordion.Body>
                                </Accordion.Item>
                            </Accordion>
                        )}
                    </div>
                </Col>
            </Row>

            {/* Products Section */}
            <div
                id="company-products-section"
                ref={productsSectionRef}
                className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3 pt-5 border-top mt-2"
            >
                <h3 className="fw-bold mb-0 d-flex align-items-center" style={{ color: '#2c3e50' }}>
                    <FaFilter className="me-2 text-muted" size={20} /> Catálogo
                </h3>
                <div className="d-flex gap-2 flex-wrap flex-grow-1 justify-content-md-end">
                    <div style={{ minWidth: '180px', flex: '1 1 auto', maxWidth: '300px' }}>
                        <CategoryFilter 
                            selectedCategory={category} 
                            onSelectCategory={handleCategoryChange} 
                        />
                    </div>
                    <div style={{ minWidth: '180px', flex: '1 1 auto', maxWidth: '300px' }}>
                        <SortSelect currentSort={sort} />
                    </div>
                </div>
            </div>

            {isLoadingProducts || isFetchingProducts ? (
                <Row className="mx-0">
                  <div className="ProductGrid">
                    {[...Array(8)].map((_, idx) => (
                      <ProductCardSkeleton key={idx} />
                    ))}
                  </div>
                </Row>
            ) : productsError ? (
                <Message variant="danger">Error al cargar los productos</Message>
            ) : products.length === 0 ? (
                <div className="text-center py-5 bg-light rounded-4 border">
                    <p className="text-muted mb-0">Esta tienda no tiene productos disponibles actualmente.</p>
                </div>
            ) : (
                <>
                    <Row className="mx-0">
                        <div className="ProductGrid">
                            {products.map((product) => (
                                <ProductCard product={product} key={product._id} />
                            ))}
                        </div>
                    </Row>
                    <Paginate
                        pages={productsData.pages}
                        page={productsData.page}
                        ruc={ruc}
                        sort={sort}
                        category={category}
                        isCompany={true}
                    />
                </>
            )}
        </Container>
    );
};

export default CompanyScreen;
